import React, { useEffect } from 'react';
import { LogoLockup } from '../components/Icons';

// Términos y Condiciones + Política de Privacidad (versión básica de lanzamiento).
// Nota: se ampliará al completar la adecuación a la Ley 21.719.
const C = { navy: '#0D1B35', blue: '#1A3A6E', line: '#DDE6F3', muted: '#4A5A72' };
// Fecha y versión de la política: el Art. 14 ter letra a de la Ley 21.719
// exige publicarlas. Actualizar ambas cada vez que cambie el texto.
const HOY = '30 de octubre de 2026';
const VERSION = '1.5';

// Términos y privacidad van en la misma página, pero /privacidad tiene que
// abrir en la política: es la dirección que se le da a Google (y a quien
// pregunta por sus datos), y antes mostraba los términos de arriba.
const esPrivacidad = () => window.location.pathname.startsWith('/privacidad') || window.location.hash === '#privacidad';

const LegalPage: React.FC = () => {
  useEffect(() => {
    document.title = esPrivacidad() ? 'Política de Privacidad · Tabularis' : 'Términos y Condiciones · Tabularis';
    if (esPrivacidad()) document.getElementById('privacidad')?.scrollIntoView();
  }, []);
  return (
  <div style={{ fontFamily: 'Inter, sans-serif', color: C.navy, background: '#fff', minHeight: '100vh' }}>
    <nav style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 6vw', borderBottom: `1px solid ${C.line}` }}>
      <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.blue, textDecoration: 'none' }}>
        <LogoLockup height={28} />
      </a>
    </nav>

    <main style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px 72px', lineHeight: 1.7, fontSize: 15 }}>
      <p style={{ color: C.muted, fontSize: 13 }}>Versión {VERSION} · Última actualización: {HOY}</p>

      <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 30, margin: '4px 0 8px' }}>Términos y Condiciones</h1>
      <p style={{ color: C.muted }}>Al crear un estudio o usar Tabularis, aceptas estos términos.</p>

      <Sec n="1" t="Qué es Tabularis">
        Tabularis es una plataforma en línea (software como servicio) para la gestión de estudios jurídicos:
        casos, plazos, pagos, documentos, equipo y consulta de estado para clientes. El servicio es operado por Tabularis
        (en adelante, "nosotros"). El registro es abierto: cualquier estudio puede crear su cuenta confirmando su correo, y queda activo de inmediato con un
        período de prueba. Nos reservamos el derecho de suspender cuentas que incumplan estos términos.
      </Sec>
      <Sec n="2" t="Tu cuenta">
        Debes entregar información veraz y mantener la confidencialidad de tus credenciales. Eres responsable de toda
        actividad realizada bajo tu cuenta. Cada cuenta es para <strong>un (1) estudio jurídico</strong>. No está permitido
        compartir, ceder ni revender el acceso a personas ajenas al estudio.
      </Sec>
      <Sec n="3" t="Uso aceptable">
        Te comprometes a usar Tabularis conforme a la ley y a no: (a) vulnerar la seguridad del sistema; (b) ingresar
        datos de terceros sin la autorización o base legal correspondiente; (c) usar el servicio para fines ilícitos.
      </Sec>
      <Sec n="4" t="Tus datos y los de tus clientes">
        La información que ingresas (incluidos los datos de tus clientes y causas) es de tu propiedad y responsabilidad.
        Tú actúas como responsable del tratamiento de esos datos frente a tus clientes; Tabularis los trata por tu encargo,
        únicamente para prestarte el servicio (ver Política de Privacidad).
      </Sec>
      <Sec n="5" t="Disponibilidad del servicio">
        Nos esforzamos por mantener el servicio disponible, pero puede haber cambios, interrupciones o
        ajustes de funciones sin previo aviso. El servicio se entrega "tal cual", sin garantías de disponibilidad
        ininterrumpida.
      </Sec>
      <Sec n="6" t="Planes y precios">
        Los planes y precios podrán ajustarse, avisándote con anticipación razonable. El servicio se cobra por plan, según el número de
        abogados y la tarifa publicada en tabularis.cl; el período de prueba es gratuito y no requiere medio de pago. La
        contratación de un plan pagado se rige por las condiciones vigentes al momento de suscribir.
      </Sec>
      <Sec n="7" t="Propiedad intelectual">
        El software, la marca y el diseño de Tabularis nos pertenecen. No adquieres derechos sobre ellos más allá del uso
        del servicio conforme a estos términos.
      </Sec>
      <Sec n="8" t="Responsabilidad">
        En la medida permitida por la ley, Tabularis no será responsable por perjuicios indirectos ni por decisiones
        profesionales que adoptes con base en el servicio o en textos generados con asistencia de IA, los cuales debes
        siempre revisar antes de usarlos.
      </Sec>
      <Sec n="9" t="Término">
        Puedes dejar de usar el servicio cuando quieras. Podemos suspender cuentas que incumplan estos términos.
      </Sec>
      <Sec n="10" t="Ley aplicable">
        Estos términos se rigen por las leyes de la República de Chile, y cualquier controversia se someterá a los
        tribunales competentes de Chile.
      </Sec>

      <hr style={{ border: 'none', borderTop: `1px solid ${C.line}`, margin: '44px 0' }} />

      <h1 id="privacidad" style={{ fontFamily: 'Playfair Display, serif', fontSize: 30, margin: '4px 0 8px', scrollMarginTop: 24 }}>Política de Privacidad</h1>
      <p style={{ color: C.muted }}>Cómo tratamos los datos personales, conforme a la Ley N° 19.628 y la Ley N° 21.719.</p>

      <Sec n="1" t="Responsable">
        Tabularis es el responsable del tratamiento de los datos de las cuentas. Para consultas sobre tus datos, escríbenos a
        <strong> contacto@tabularis.cl</strong>.
      </Sec>
      <Sec n="2" t="Qué datos tratamos">
        (a) <strong>De tu cuenta</strong>: nombre, correo y datos del estudio. (b) <strong>De uso</strong>: registros técnicos
        y de auditoría para seguridad. (c) <strong>Contenido que ingresas</strong>: datos de casos, clientes, pagos y
        documentos, que tratamos por tu encargo.
      </Sec>
      <Sec n="3" t="Para qué los usamos">
        Únicamente para prestarte el servicio: operar tu panel, autenticarte, resguardar la seguridad, dar soporte y, cuando
        lo solicitas, asistirte con la redacción mediante IA. No vendemos tus datos ni los usamos con fines publicitarios.
      </Sec>
      <Sec n="4" t="Dónde se alojan y seguridad">
        Los datos se alojan en infraestructura de <strong>Amazon Web Services</strong>, en su región de Sudamérica
        (<strong>São Paulo, Brasil</strong>), con cifrado en tránsito y en reposo, <strong>aislamiento por estudio</strong> a
        nivel de base de datos y acceso restringido.
      </Sec>
      <Sec n="4 bis" t="Transferencia internacional de datos">
        Tabularis está constituida en Chile, por lo que el tratamiento de estos datos queda sujeto a la
        <strong> Ley 21.719</strong> con independencia del país donde se encuentren los servidores.
        A la fecha, Brasil no cuenta con una declaración de nivel adecuado de protección emitida por la Agencia de Protección
        de Datos Personales. Esta transferencia se ampara en las garantías contractuales del acuerdo de tratamiento de datos
        suscrito con Amazon Web Services. Está planificada la migración a la región de AWS en Chile cuando esté disponible.
      </Sec>
      <Sec n="5" t="Terceros que nos ayudan">
        Usamos proveedores que actúan por nuestro encargo: <strong>Amazon Web Services</strong> (alojamiento) y{' '}
        <strong>Anthropic</strong> (para el redactor con IA: cuando usas esa función, el texto se procesa para generar la
        respuesta y no se emplea para entrenar modelos). Los pagos con tarjeta se procesan en el portal del Banco de Chile (Banchile Pagos): tus datos de tarjeta no pasan por Tabularis.
      </Sec>
      {/* Google pide esta sección, con la declaración de "uso limitado", para
          aprobar el acceso a Drive. Si cambia lo que se hace con los archivos de
          Drive (por ejemplo, leer carpetas completas), hay que actualizarla. */}
      <Sec n="5 bis" t="Archivos de Google Drive">
        Si eliges traer documentos desde Google Drive, Tabularis te pide permiso para acceder{' '}
        <strong>solo a los archivos y carpetas que tú selecciones</strong> en el selector de Google; no puede ver el resto
        de tu Drive. Esos archivos se descargan en tu navegador y se guardan como documentos de los casos que indiques, igual
        que si los subieras desde tu computador. No guardamos tu sesión de Google ni modificamos ni borramos nada en tu Drive,
        y el permiso caduca al cerrar la ventana. El uso y la transferencia de la información recibida de las API de Google
        se ajustan a la{' '}
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
          Política de Datos de Usuario de los Servicios de API de Google</a>, incluidos sus requisitos de uso limitado.
      </Sec>
      {/* Debe calzar con la lista de páginas de frontend/public/meta-pixel.js:
          si el píxel se carga en otra página, hay que decirlo acá. */}
      <Sec n="5 ter" t="Medición de anuncios en nuestras páginas públicas">
        En las páginas públicas de tabularis.cl (la portada, la página de Chatbularis, las cápsulas de
        aprendizaje y estos textos legales) usamos el <strong>píxel de Meta</strong> (Facebook e Instagram) para saber
        cuántas personas llegan desde nuestros anuncios y medir su resultado. El píxel guarda cookies en tu navegador y
        envía a Meta la dirección de la página que visitas, datos técnicos de tu navegador y tu dirección IP. Meta trata
        esa información como responsable, según su{' '}
        <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">política de privacidad</a>.{' '}
        <strong>El píxel no se carga en el panel de los estudios, en Chatbularis ni en la consulta de estado de los
        clientes</strong>: nada de lo que ingresas en Tabularis (casos, clientes, documentos o pagos) llega a Meta. Puedes
        bloquearlo desde la configuración de cookies de tu navegador o en las preferencias de anuncios de tu cuenta de Meta.
      </Sec>
      <Sec n="6" t="Tus derechos">
        Puedes ejercer tus derechos de acceso, rectificación, cancelación y oposición escribiéndonos a{' '}
        <strong>contacto@tabularis.cl</strong>. Responderemos en los plazos legales.
      </Sec>
      <Sec n="7" t="Conservación">
        Conservamos los datos mientras tengas una cuenta activa y luego el tiempo necesario para cumplir obligaciones
        legales. Puedes solicitar la eliminación de tu estudio y sus datos.
      </Sec>
      <Sec n="8" t="Cambios">
        Podemos actualizar esta política; publicaremos la versión vigente en esta página con su fecha.
      </Sec>

      <p style={{ color: C.muted, fontSize: 13, marginTop: 40 }}>
        Tabularis · tabularis.cl · Gestión legal para estudios jurídicos de Chile
      </p>
    </main>
  </div>
  );
};

const Sec: React.FC<{ n: string; t: string; children: React.ReactNode }> = ({ n, t, children }) => (
  <section style={{ marginTop: 22 }}>
    <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 6px' }}>{n}. {t}</h2>
    <p style={{ margin: 0, color: '#33425A' }}>{children}</p>
  </section>
);

export default LegalPage;
